import { styles } from "@repo/helpers";
import { Button, ButtonTypes } from "@repo/ui";
import { ArrowUp, CornerLeftUp } from "lucide-react";

import type { SettingGridRow } from "../settingsTree";

import { chainLink } from "../settingsTree";
import { SourcePills } from "./sourcePills";
import style from "./style.module.scss";
import { ValueText } from "./valueText";

interface Props {
	row: SettingGridRow;
	/**
	 * The arrow from this level up to the one it overrides or inherits from: the level above in the chain, or the opened
	 * setting for a child. None for the top level
	 */
	arrow?: "chain" | "child";
	/** The level whose row gives the opened setting its value */
	active?: boolean;
	/** The opened setting itself, which isn't a link */
	current?: boolean;
	onOpen: (name: string) => void;
}

const LINK_LABELS = { overrides: "overrides", inheritedFrom: "inherited from" };

const LINK_TITLES = {
	overrides: "Has its own row for the segment, which replaces the value above",
	inheritedFrom: "Has no row for the segment, so it uses the value above",
};

/** One level of a setting's hierarchy: how it gets its value, from that level's point of view, then its name and value */
export function ChainLevel({ row, arrow, active, current, onOpen }: Props) {
	const link = chainLink(row);
	// Points from this level to the one above, since the label says what this level does to it
	const Icon = arrow === "child" ? CornerLeftUp : ArrowUp;

	return (
		<div className={styles(style.chainLevel, active ? style.chainActive : "")}>
			{arrow && (
				<span
					className={link === "overrides" ? style.overridesLink : style.inheritedLink}
					title={LINK_TITLES[link]}
				>
					<Icon className={style.chainIcon} />
					{LINK_LABELS[link]}
				</span>
			)}
			{current ? (
				<span className={style.settingName}>{row.name}</span>
			) : (
				<Button types={[ButtonTypes.Text]} title="Show this setting" onClick={() => onOpen(row.name)}>
					<span className={style.settingName}>{row.name}</span>
				</Button>
			)}
			<ValueText value={row.value} enumOptions={row.enumOptions} charLimit={60} masked={row.winner?.isMasked} />
			{/* A level that inherits has no source of its own; the top level shows its own row or its default */}
			{(link === "overrides" || !arrow) && <SourcePills row={row} />}
			{current && <span className={style.hint}>This setting</span>}
		</div>
	);
}
